import HeadlessButton from "@components/Button";
import HeadlessDialog from "@components/Dialog";
import ScrollableModalBody from "@components/ScrollableModalBody";
import { RiMoneyDollarCircleLine } from "react-icons/ri";
import { IPayrollAddition } from "@interfaces/IPayrollAdditionDTO";
import axiosClient from "@service/axiosClient";
import { Form, Formik } from "formik";
import React from "react";
import * as Yup from "yup";
import Spinner from "@components/Spinner";
import { toISODate } from "@utils/date";
import Snackbar from "@components/constant_components/Snackbar";

const validationSchema = Yup.object().shape({
  amount: Yup.number()
    .positive("Amount must be positive")
    .required("Amount is required"),
  start_date: Yup.date().required("Start date is required"),
  end_date: Yup.date()
    .nullable()
    .min(Yup.ref("start_date"), "End date must be after start date"),
  recurring: Yup.boolean().required("recurring is required"),
  type: Yup.string()
    .oneOf(
      ["disability", "bonus", "reimbursement", "check_correction", "other"],
      "Invalid type"
    )
    .required("Type is required"),
});

interface ContractorPayrollAdditionsProps {
  workerId: number; // internal worker record id
  contractorId: number; // external contractor id (display)
  contractorName: string;
  isOpen: boolean;
  onClose: () => void;
  onAdditionSaved: () => void;
}

interface EditableAdditionCardProps {
  addition: IPayrollAddition;
  onUpdate: (id: number, data: Partial<IPayrollAddition>) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
}

const EditableAdditionCard: React.FC<EditableAdditionCardProps> = ({
  addition,
  onUpdate,
  onDelete,
}) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [editData, setEditData] = React.useState({
    amount: addition.amount,
    start_date: toISODate(addition.start_date) || "",
    end_date: toISODate(addition.end_date) || "",
    type: addition.type,
    recurring: addition.recurring,
  });
  const [isUpdating, setIsUpdating] = React.useState(false);

  const handleSave = async () => {
    setIsUpdating(true);
    try {
      await onUpdate(addition.id, {
        ...editData,
        amount: editData.amount,
      });
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating addition:", error);
      alert("Failed to update payroll addition");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancel = () => {
    setEditData({
      amount: addition.amount,
      start_date: toISODate(addition.start_date) || "",
      end_date: toISODate(addition.end_date) || "",
      type: addition.type,
      recurring: addition.recurring,
    });
    setIsEditing(false);
  };

  const handleDelete = async () => {
    if (
      window.confirm("Are you sure you want to delete this payroll addition?")
    ) {
      try {
        await onDelete(addition.id);
      } catch (error) {
        console.error("Error deleting addition:", error);
        alert("Failed to delete payroll addition");
      }
    }
  };

  if (isEditing) {
    return (
      <div className="tw-p-4 tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg">
        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-mb-4">
          {/* Type */}
          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
              Type
            </label>
            <select
              value={editData.type}
              onChange={(e) =>
                setEditData({ ...editData, type: e.target.value as any })
              }
              className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-text-sm"
            >
              <option value="disability">Disability</option>
              <option value="bonus">Bonus</option>
              <option value="reimbursement">Reimbursement</option>
              <option value="check_correction">Check Correction</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Amount */}
          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
              Amount ($)
            </label>
            <input
              type="number"
              step="0.01"
              value={editData.amount}
              onChange={(e) =>
                setEditData({ ...editData, amount: e.target.value })
              }
              className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-text-sm"
            />
          </div>

          {/* Start Date */}
          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={editData.start_date}
              onChange={(e) =>
                setEditData({ ...editData, start_date: e.target.value })
              }
              className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-text-sm"
            />
          </div>

          {/* End Date */}
          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
              End Date (Optional)
            </label>
            <input
              type="date"
              value={editData.end_date}
              onChange={(e) => {
                setEditData({
                  ...editData,
                  end_date: e.target.value,
                  recurring: e.target.value ? false : editData.recurring,
                });
              }}
              className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-text-sm"
            />
          </div>
        </div>

        {/* Recurring Checkbox */}
        <div className="tw-flex tw-items-center tw-mb-4">
          <input
            type="checkbox"
            checked={editData.recurring}
            onChange={(e) => {
              setEditData({
                ...editData,
                recurring: e.target.checked,
                end_date: e.target.checked ? "" : editData.end_date,
              });
            }}
            disabled={!!editData.end_date}
            className="tw-h-4 tw-w-4 tw-text-blue-600 focus:tw-ring-blue-500 tw-border-gray-300 tw-rounded"
          />
          <label className="tw-ml-2 tw-text-sm tw-font-medium tw-text-gray-700">
            Recurring Addition{" "}
            {!!editData.end_date && "(Disabled - End date is set)"}
          </label>
        </div>

        {/* Action Buttons */}
        <div className="tw-flex tw-justify-end tw-space-x-2">
          <button
            type="button"
            onClick={handleCancel}
            className="tw-px-3 tw-py-1 tw-text-sm tw-text-gray-600 tw-border tw-border-gray-300 tw-rounded-md hover:tw-bg-gray-50"
          >
            Cancel
          </button>
          <HeadlessButton
            onClick={handleSave}
            disabled={isUpdating}
            className="tw-px-3 tw-py-1 tw-text-sm"
          >
            {isUpdating ? <Spinner className="tw-w-4 tw-h-4" /> : "Save"}
          </HeadlessButton>
        </div>
      </div>
    );
  }

  return (
    <div className="tw-p-4 tw-bg-gray-50 tw-border tw-border-gray-100 tw-rounded-lg">
      <div className="tw-flex tw-justify-between tw-items-start tw-mb-2">
        <div className="tw-flex-1">
          <div className="tw-flex tw-justify-between tw-items-center tw-mb-2">
            <span className="tw-font-medium tw-text-gray-900 tw-capitalize">
              {addition.type?.replace("_", " ") || "Unknown"}
            </span>
            <span className="tw-text-lg tw-font-semibold tw-text-green-600">
              ${addition.amount || "0.00"}
            </span>
          </div>
          <div className="tw-text-sm tw-text-gray-600">
            <span className="tw-font-medium">Start:</span>{" "}
            {addition.start_date ?? "N/A"}
            {addition.end_date && (
              <>
                {" | "}
                <span className="tw-font-medium">End:</span> {addition.end_date}
              </>
            )}
            {addition.recurring && (
              <span className="tw-ml-2 tw-px-2 tw-py-1 tw-bg-blue-100 tw-text-blue-800 tw-rounded-full tw-text-xs tw-font-medium">
                Recurring
              </span>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="tw-flex tw-space-x-2 tw-ml-4">
          <button
            onClick={() => setIsEditing(true)}
            className="tw-p-1 tw-text-blue-600 hover:tw-text-blue-800 tw-text-sm"
            title="Edit"
          >
            Edit
          </button>
          <button
            onClick={handleDelete}
            className="tw-p-1 tw-text-red-600 hover:tw-text-red-800 tw-text-sm"
            title="Delete"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const initialFormValues = {
  amount: "",
  start_date: toISODate(new Date()) ?? "",
  end_date: "",
  recurring: false,
  type: "bonus" as const,
};

export default function ContractorPayrollAdditions({
  workerId,
  contractorId,
  contractorName,
  isOpen,
  onClose,
  onAdditionSaved,
}: ContractorPayrollAdditionsProps) {
  const [isLoading, setIsLoading] = React.useState(false);
  const [existingAdditions, setExistingAdditions] = React.useState<
    IPayrollAddition[]
  >([]);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [snackbarMessage, setSnackbarMessage] = React.useState<string>("");
  const [showSnackbar, setShowSnackbar] = React.useState<boolean>(false);

  const showSnackbarMessage = React.useCallback((message: string) => {
    setSnackbarMessage(message);
    setShowSnackbar(true);
    setTimeout(() => setShowSnackbar(false), 3000); // Auto-hide after 3 seconds
  }, []);

  React.useEffect(() => {
    if (!isOpen || !workerId) return;

    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        // console.log(`Fetching payroll additions for worker ID: ${workerId}`);
        const response = await axiosClient.get(
          `/payroll_additions_api/worker/${workerId}/all`
        );

        // Debug: Log the response to see what we're getting
        // console.log("API Response:", response);
        // console.log("API Response Data:", response.data);
        // console.log("Response type:", typeof response.data);
        // console.log("Is Array:", Array.isArray(response.data));

        // Ensure we always have an array
        const additions = Array.isArray(response.data) ? response.data : [];
        // console.log("Processed additions:", additions);
        setExistingAdditions(additions);

        // If no additions found, log it
        // if (additions.length === 0) {
        //   console.log(
        //     `No active payroll additions found for worker ${workerId}`
        //   );
        // }
      } catch (error: any) {
        // console.error("Error fetching payroll additions:", error);
        // console.error("Error response:", error.response);
        setError(
          `Failed to load existing payroll additions: ${
            error.response?.status || "Unknown error"
          }`
        );
        // Ensure we set an empty array on error
        setExistingAdditions([]);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [isOpen, workerId]);

  const handleSubmit = React.useCallback(
    async (
      values: typeof initialFormValues,
      {
        setSubmitting,
        resetForm,
      }: import("formik").FormikHelpers<typeof initialFormValues>
    ) => {
      setError(null);
      setSuccess(null);

      try {
        const payload = {
          ...values,
          worker_id: workerId,
          amount: parseFloat(values.amount),
          end_date: values.recurring
            ? values.end_date || null
            : values.start_date,
        };

        // DEBUG: Log what we're sending to the backend
        // console.log(
        //   "Frontend sending payload:",
        //   JSON.stringify(payload, null, 2)
        // );

        await axiosClient.post(
          `/payroll_additions_api/worker/${workerId}/create_payroll_addition`,
          payload
        );
        setSuccess("Payroll addition created successfully!");

        // Refresh the existing additions list
        const response = await axiosClient.get(
          `/payroll_additions_api/worker/${workerId}/all`
        );
        const additions = Array.isArray(response.data) ? response.data : [];
        setExistingAdditions(additions);

        resetForm();
        onAdditionSaved();
      } catch (error: any) {
        console.error("Error creating payroll addition:", error);
        setError(
          error.response?.data?.message ||
            error.response?.data?.error ||
            "Failed to create payroll addition"
        );
      } finally {
        setSubmitting(false);
      }
    },
    [workerId, onAdditionSaved]
  );

  const handleUpdateAddition = React.useCallback(
    async (additionId: number, updateData: Partial<IPayrollAddition>) => {
      try {
        const payload = {
          ...updateData,
          amount: parseFloat(updateData.amount || "0"),
          end_date: updateData.end_date || null,
        };

        // DEBUG: Log what we're sending for update
        // console.log(
        //   "Frontend update payload:",
        //   JSON.stringify(payload, null, 2)
        // );
        // console.log(
        //   "Update end date specifically:",
        //   updateData.end_date,
        //   typeof updateData.end_date
        // );

        await axiosClient.put(`/payroll_additions_api/${additionId}`, payload);

        // Refresh the list
        const response = await axiosClient.get(
          `/payroll_additions_api/worker/${workerId}/all`
        );
        const additions = Array.isArray(response.data) ? response.data : [];
        setExistingAdditions(additions);

        setSuccess("Payroll addition updated successfully!");
        setError(null);
        showSnackbarMessage("Update applied");
      } catch (error: any) {
        console.error("Error updating payroll addition:", error);
        setError(
          error.response?.data?.error || "Failed to update payroll addition"
        );
        setSuccess(null);
      }
    },
    [workerId, showSnackbarMessage]
  );

  const handleDeleteAddition = React.useCallback(
    async (additionId: number) => {
      try {
        await axiosClient.delete(`/payroll_additions_api/${additionId}/delete`);

        // Refresh the list
        const response = await axiosClient.get(
          `/payroll_additions_api/worker/${workerId}/all`
        );
        const additions = Array.isArray(response.data) ? response.data : [];
        setExistingAdditions(additions);

        setSuccess("Payroll addition deleted successfully!");
        setError(null);
        showSnackbarMessage("Delete successful");
      } catch (error: any) {
        console.error("Error deleting payroll addition:", error);
        setError(
          error.response?.data?.error || "Failed to delete payroll addition"
        );
        setSuccess(null);
      }
    },
    [workerId, showSnackbarMessage]
  );

  if (!isOpen) return null;

  return (
    <HeadlessDialog
      title={
        <div className="tw-flex tw-items-center tw-gap-2">
          <RiMoneyDollarCircleLine
            className="tw-text-primary-light"
            size={24}
          />
          <span>{`Payroll Additions - ${contractorName} (Contractor ID: ${contractorId})`}</span>
        </div>
      }
      description="Manage payroll additions for this contractor"
      isOpen={isOpen}
      setIsOpen={(open: boolean) => {
        if (!open) onClose();
      }}
      onClose={onClose}
      buttonText="Close"
      size="tw-w-[100vw] xl:tw-max-w-[60vw]"
    >
      {isLoading ? (
        <div className="tw-p-6">
          <Spinner className="tw-flex tw-justify-center tw-items-center" />
        </div>
      ) : (
        <ScrollableModalBody className="tw-overflow-y-scroll">
          <div className="tw-space-y-6 tw-ml-[2px]">
            {/* Error/Success Messages */}
            {error && (
              <div className="tw-p-4 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-text-red-700">
                {error}
              </div>
            )}
            {success && (
              <div className="tw-p-4 tw-bg-green-50 tw-border tw-border-green-200 tw-rounded-lg tw-text-green-700">
                {success}
              </div>
            )}

            {/* Show existing additions */}
            <div className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6">
              <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-4">
                All Payroll Additions
              </h3>

              {Array.isArray(existingAdditions) &&
              existingAdditions.length > 0 ? (
                <div className="tw-space-y-3">
                  {existingAdditions.map((addition) => (
                    <EditableAdditionCard
                      key={addition.id}
                      addition={addition}
                      onUpdate={handleUpdateAddition}
                      onDelete={handleDeleteAddition}
                    />
                  ))}
                </div>
              ) : (
                <div className="tw-text-center tw-py-8 tw-text-gray-500">
                  <div className="tw-text-sm">
                    No active payroll additions found for this worker.
                  </div>
                  <div className="tw-text-xs tw-mt-1">
                    Add a new payroll addition below.
                  </div>
                </div>
              )}
            </div>

            {/* Form for adding new addition */}
            <div className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6">
              <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-4">
                Add New Payroll Addition
              </h3>

              <Formik
                initialValues={initialFormValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({
                  values,
                  handleChange,
                  handleBlur,
                  errors,
                  touched,
                  isSubmitting,
                }) => (
                  <Form className="tw-space-y-4">
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                      {/* Type Field */}
                      <div>
                        <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                          Type
                        </label>
                        <select
                          name="type"
                          value={values.type}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-shadow-sm focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-blue-500 tw-text-sm"
                        >
                          <option value="disability">Disability</option>
                          <option value="bonus">Bonus</option>
                          <option value="reimbursement">Reimbursement</option>
                          <option value="check_correction">
                            Check Correction
                          </option>
                          <option value="other">Other</option>
                        </select>
                        {errors.type && touched.type && (
                          <div className="tw-text-red-500 tw-text-sm tw-mt-1">
                            {errors.type}
                          </div>
                        )}
                      </div>

                      {/* Amount Field */}
                      <div>
                        <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                          Amount ($)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          name="amount"
                          value={values.amount}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-shadow-sm focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-blue-500 tw-text-sm"
                          placeholder="0.00"
                        />
                        {errors.amount && touched.amount && (
                          <div className="tw-text-red-500 tw-text-sm tw-mt-1">
                            {errors.amount}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                      {/* Start Date Field */}
                      <div>
                        <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                          Start Date
                        </label>
                        <input
                          type="date"
                          name="start_date"
                          value={values.start_date}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-shadow-sm focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-blue-500 tw-text-sm"
                        />
                        {errors.start_date && touched.start_date && (
                          <div className="tw-text-red-500 tw-text-sm tw-mt-1">
                            {errors.start_date}
                          </div>
                        )}
                      </div>

                      {/* Addition Type Selection */}
                      <div>
                        <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                          Addition Type
                        </label>
                        <div className="tw-space-y-2">
                          <label className="tw-flex tw-items-center">
                            <input
                              type="radio"
                              name="addition_type"
                              value="recurring"
                              checked={values.recurring}
                              onChange={() => {
                                handleChange({
                                  target: { name: "recurring", value: true },
                                });
                              }}
                              className="tw-h-4 tw-w-4 tw-text-blue-600 focus:tw-ring-blue-500 tw-border-gray-300"
                            />
                            <span className="tw-ml-2 tw-text-sm tw-text-gray-700">
                              Recurring
                            </span>
                          </label>
                          <label className="tw-flex tw-items-center">
                            <input
                              type="radio"
                              name="addition_type"
                              value="one_time"
                              checked={!values.recurring}
                              onChange={() => {
                                handleChange({
                                  target: { name: "recurring", value: false },
                                });
                                handleChange({
                                  target: { name: "end_date", value: "" },
                                });
                              }}
                              className="tw-h-4 tw-w-4 tw-text-blue-600 focus:tw-ring-blue-500 tw-border-gray-300"
                            />
                            <span className="tw-ml-2 tw-text-sm tw-text-gray-700">
                              One-time
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* End Date Field - Only show for recurring */}
                    {values.recurring && (
                      <div>
                        <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                          End Date
                        </label>
                        <input
                          type="date"
                          name="end_date"
                          value={values.end_date}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-shadow-sm focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-blue-500 tw-text-sm"
                        />
                        {errors.end_date && touched.end_date && (
                          <div className="tw-text-red-500 tw-text-sm tw-mt-1">
                            {errors.end_date}
                          </div>
                        )}
                        <div className="tw-text-xs tw-text-gray-500 tw-mt-1">
                          Optional - leave blank for indefinite recurring
                        </div>
                      </div>
                    )}
                    {/* Submit Button */}
                    <div className="tw-flex tw-justify-end tw-mt-6 tw-pt-4 tw-border-t tw-border-gray-200">
                      <HeadlessButton
                        type="submit"
                        disabled={isSubmitting}
                        className="md:tw-w-[30%] tw-h-10"
                      >
                        {isSubmitting ? (
                          <Spinner className="tw-flex tw-justify-center tw-items-center" />
                        ) : (
                          "Add Payroll Addition"
                        )}
                      </HeadlessButton>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </ScrollableModalBody>
      )}

      {/* Snackbar for success messages */}
      <Snackbar message={snackbarMessage} isActive={showSnackbar} />
    </HeadlessDialog>
  );
}
